import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Avatar,
  ButtonDS,
  NotInterestedOutlined,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
} from "@qivia/ui";
import { useAppSelector } from "../../../../redux/hooks";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LittleCardAsset } from "@qivia/ui/src/assets/assets";
import "../../../../i18n/i18n";
import { DriverDeleteModal } from "../driverDeleteModal";
import { selectDriversList } from "../driversSlice";
import { AnalyticCodesSection } from "./analyticCodesSection";
import { EcoDrivingScoreSection } from "./ecoDrivingScoreSection";
import { VehicleSection } from "./vehicleSection";
import { UpdateDriverStatusSection } from "./updateDriverStatusSection";

type DriversSidePanelProps = {
  canDeactivate?: boolean;
  canUpdateAnalyticCode?: boolean;
  canUpdateStatus?: boolean;
  hasAdvancedAnalytics?: boolean;
};

export const DriversSidePanel: FunctionComponent<DriversSidePanelProps> = (
  props,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const driversList = useAppSelector(selectDriversList);
  const driver = useMemo(
    () => driversList.find((d) => d.uuid === params.id),
    [driversList, params.id],
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const firstPath = location.pathname.split("/")[1]; // home or departments

  useEffect(() => {
    if (!driver) {
      navigate(`/${firstPath}/drivers`);
    }
  }, [navigate, driver, firstPath]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/drivers`);
  }, [firstPath, navigate]);

  const initials = driver && driver.name.split(" ").map((row) => row.charAt(0));
  const isDriverBlocked = useMemo(
    () => driver && driver.status === "BLOCKED",
    [driver],
  );
  const isDriverDelete = useMemo(
    () => driver && driver.status === "DELETE",
    [driver],
  );

  if (!driver) {
    return;
  }

  return (
    <SidePanelDS.SidePanel
      visible={!!driver}
      onClose={onSidePanelClosed}
      absoluteSection={
        props.canDeactivate ? (
          <ButtonDS
            leftIcon={
              <StyledIconColor $isDelete={isDriverDelete ?? false}>
                <NotInterestedOutlined />
              </StyledIconColor>
            }
            text={t("drivers.sidePanel.opposition")}
            format={"fill"}
            buttonType={"primary"}
            onClick={() =>
              !isDriverDelete && setIsDeleteModalVisible(!isDeleteModalVisible)
            }
            iconColor={"errorNormal"}
            disabled={isDriverDelete}
          />
        ) : undefined
      }
    >
      <SidePanelDS.Section
        verticalSpacing={2}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <StyledPanelContainer>
          {driver.status === "ACTIVE" ? (
            <Tag
              text={t(`drivers.status.${driver.status}`)}
              backgroundColor={
                colors["colors/system/success/success_ultraLight"]
              }
              textColor={colors["colors/system/success/success_normal"]}
            />
          ) : (
            <Tag
              text={t(`drivers.status.${driver.status}`)}
              backgroundColor={
                colors["colors/surfaces/background/background_level2"]
              }
              textColor={colors["colors/text/black"]}
            />
          )}
          <Spacer y={1.5} />
          <Avatar
            size={"XL"}
            color={"mint"}
            firstName={initials ? initials[0] : ""}
            lastName={initials ? initials[initials.length - 1] : ""}
          />
          <Spacer y={1.5} />
          <StyledName>{driver.name}</StyledName>
          <Spacer y={1.5} />
          {props.canUpdateStatus && (
            <UpdateDriverStatusSection
              driverUuid={driver.uuid}
              isDriverDelete={isDriverDelete ?? false}
              isDriverBlocked={isDriverBlocked ?? false}
            />
          )}
          {(driver.cardUuid || driver.vehicleUuid) && (
            <>
              <Spacer y={1.5} />
              <SidePanelDS.ActionBlock>
                <>
                  {driver.cardUuid && (
                    <SidePanelDS.SectionedActionStripe
                      left={
                        <StyledRow>
                          <LittleCardAsset />
                          <Spacer x={1} />
                          {`**** ${driver.cardLast4Digits?.toString()}`}
                        </StyledRow>
                      }
                      right={
                        <StyledLittleIcon>
                          <RemoveRedEyeOutlined
                            key="card"
                            onClick={() =>
                              navigate(
                                `/${firstPath}/cards/cards/${driver.cardUuid}?fromNavigate=true`,
                              )
                            }
                          />
                          <Spacer x={1} />
                        </StyledLittleIcon>
                      }
                    />
                  )}
                  {driver.vehicleUuid && (
                    <SidePanelDS.SectionedActionStripe
                      left={
                        <StyledRowCenter>
                          {driver.registrationNumber}
                          {driver.vehicleReference && (
                            <>
                              <Spacer x={1} />
                              <Tag
                                textColor={colors["colors/text/darkGrey"]}
                                text={driver.vehicleReference}
                                backgroundColor={
                                  colors[
                                    "colors/surfaces/background/background_level0"
                                  ]
                                }
                                borderColor={colors["colors/text/darkGrey"]}
                              />
                            </>
                          )}
                        </StyledRowCenter>
                      }
                      right={
                        <StyledLittleIcon>
                          <RemoveRedEyeOutlined
                            key="vehicle"
                            onClick={() =>
                              navigate(
                                `/${firstPath}/vehicles/${driver.vehicleUuid}?fromNavigate=true`,
                              )
                            }
                          />
                          <Spacer x={1} />
                        </StyledLittleIcon>
                      }
                    />
                  )}
                </>
              </SidePanelDS.ActionBlock>
            </>
          )}
        </StyledPanelContainer>
      </SidePanelDS.Section>
      {props.canUpdateAnalyticCode ? (
        <AnalyticCodesSection
          isDriverDelete={isDriverDelete ?? false}
          driverAnalyticCode1={driver.analyticCode1}
          driverAnalyticCode2={driver.analyticCode2}
          driverUuid={driver.uuid}
          driversList={driversList}
        />
      ) : (
        <></>
      )}
      <EcoDrivingScoreSection
        driver={driver}
        hasAdvancedAnalytics={props.hasAdvancedAnalytics ?? false}
      />
      <VehicleSection driver={driver} firstPath={firstPath} />
      <DriverDeleteModal
        isVisible={isDeleteModalVisible}
        onCloseModal={() => {
          setIsDeleteModalVisible(false);
        }}
        driverUuid={driver.uuid}
      />
    </SidePanelDS.SidePanel>
  );
};

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledName = styled.div`
  ${typographies["Header/H1"]};
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledIconColor = styled.div<{ $isDelete: boolean }>`
  & svg {
    fill: ${({ $isDelete }) => ($isDelete ? "grey" : "")};
  }
`;

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;
